import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PrimaryButton from "../components/PrimaryButton";
import useDataFetch from "../hooks/useDataFetch";
import Dropdown from "../components/Dropdown";
import { MobileResponsive } from "./MobileResponsive";
import { Map } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker";
import { InfoWindowPopup } from "../components/InfoWindow";
import { getDefaultCenter, getEntityData } from "../utils/mapUtils";
import { clearUnsubscribe } from "../features/driverSlice";
import { useDispatch } from "react-redux";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";

const Outlets = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = searchParams.get("st");

  const [currentPage, setCurrentPage] = useState(1);
  const [searchName, setSearchName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(
    params === "active" ? "active" : ""
  );

  const [inputStates, setInputStates] = useState({
    searchOutlet: { isFocused: false, isFilled: false, value: "" },
  });

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage);
    if (searchName.trim()) params.append("name", searchName.trim());
    if (selectedStatus) params.append("status", selectedStatus);
    return `/admin/outlets?${params.toString()}`;
  };

  // Fetch data for outlets
  const { data, isLoading } = useDataFetch(buildApiUrl());

  const { data: newOutlets } = useDataFetch("/admin/outlet-onboarding-requests");

  const outlets = data?.outlets || [];
  const totalOutletsCount = data?.totalOutletsCount || 0;
  const activeOutletsCount = data?.activeOutletsCount || 0;
  const totalPages = data?.totalPages || 1;
  const mapCenter = useMemo(
    () => getDefaultCenter(outlets, "outlets"),
    [outlets]
  );
  const mapKey = `${mapCenter.lat}-${mapCenter.lng}-${outlets.length}`;

  /* Google Maps */
  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = (id, marker, entityType) => {
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, data),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }

    return () => {
      dispatch(clearUnsubscribe());
    };
  }, [selectedMarker, dispatch]);

  const handleInputChange = (event, inputField) => {
    const value = event.target.value;

    setInputStates((prevState) => ({
      ...prevState,
      [inputField]: {
        ...prevState[inputField],
        value: value,
        isFilled: value !== "",
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchName(inputStates.searchOutlet.value.trim());
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setInputStates((prevState) => ({
      ...prevState,
      searchOutlet: { ...prevState.searchOutlet, value: "", isFilled: false },
    }));
    setSearchName("");
    setCurrentPage(1);
  };

  const handleStatusChange = (option) => {
    const status = option === "Active Outlets" ? "active" : "";
    setSelectedStatus(status);
    setCurrentPage(1);
    if (status === "active") {
      setSearchParams("st=active");
    } else {
      setSearchParams("");
    }
  };

  // Handles click on an outlet to navigate to its page
  const handleOutletClick = (outletId) => {
    navigate(`/outlets/${outletId}`);
  };

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Streamline Your Outlet Management"
        pageDescription="Take control of your outlets with Beep! Effortlessly manage new outlet requests, view detailed outlet information, track payment history, and set pickup prices. Enable or disable outlets with a click!"
        pageKeywords="Beep, outlet management, new outlet requests, outlet details, payment history, set pickup prices, enable outlets, disable outlets, active outlets"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary flex items-start">
          <div className="flex flex-col w-full">
            <div className="flex justify-between w-[95.5%]">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-[20px]">
                  {selectedStatus === "active"
                    ? `Active Outlets: ${activeOutletsCount}`
                    : `Total Outlets: ${totalOutletsCount}`}
                </span>
              </div>

              {/* Search Bar */}
              <div className="flex items-center gap-3">
                <form
                  action=""
                  className="flex items-center gap-2"
                  onSubmit={handleSubmit}
                >
                  <div className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] 2xl:w-[20vw] xl:w-[16vw] lg:w-[16vw]">
                    <div className="relative w-full">
                      <input
                        type="text"
                        placeholder="Search outlet name"
                        className={`peer rounded-full 2xl:py-1.5 xl:py-1 lg:py-0 text-[#F7631D] placeholder:text-transparent 2xl:text-base xl:text-base lg:text-xs font-medium w-full outline-none px-1 placeholder:select-none`}
                        value={inputStates.searchOutlet.value}
                        onInput={(e) => handleInputChange(e, "searchOutlet")}
                        onFocus={() =>
                          setInputStates((prev) => ({
                            ...prev,
                            searchOutlet: { ...prev.searchOutlet, isFocused: true },
                          }))
                        }
                        onBlur={() =>
                          setInputStates((prev) => ({
                            ...prev,
                            searchOutlet: { ...prev.searchOutlet, isFocused: false },
                          }))
                        }
                      />
                      <label
                        className={`absolute left-1 transform transition-all duration-200 ${inputStates.searchOutlet.isFocused ||
                          inputStates.searchOutlet.isFilled
                          ? "-top-0.5 -translate-y-1/2 2xl:left-2 xl:left-1 lg:left-0  2xl:text-xs xl:text-[11px] lg:text-[9px] px-1 bg-white rounded-full"
                          : "top-1/2 -translate-y-1/2 2xl:left-3 xl:left-1 lg:left-0.5 2xl:text-base xl:text-[14px] lg:text-xs font-medium"
                          } text-[#F7631D] pointer-events-none`}
                      >
                        Search outlet name
                      </label>
                    </div>
                    {inputStates.searchOutlet.value && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="text-[#F7631D] hover:text-[#d55419] text-xl"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="bg-[#F7631D] text-white px-4 py-1.5 rounded-full font-semibold hover:bg-[#d55419] transition-colors flex items-center gap-2 2xl:text-base xl:text-sm lg:text-xs"
                  >
                    <img
                      src="/assets/search.svg"
                      alt=""
                      className="2xl:w-4 2xl:h-4 xl:w-3 xl:h-3 lg:w-3 lg:h-3 brightness-0 invert"
                    />
                    Search
                  </button>
                </form>
              </div>

              {/* Outlet Info */}
              <div className="flex items-center gap-4 text-[#413F3F]">
                <Dropdown
                  options={["Active Outlets", "Total Outlets"]}
                  selected={
                    selectedStatus === "active"
                      ? "Active Outlets"
                      : "Total Outlets"
                  }
                  onSelect={handleStatusChange}
                  label="Select Option"
                  mutation={true}
                />
              </div>
            </div>

            {/* Outlets Grid */}
            <div
              className={`grid ${
                isLoading ? "grid-cols-1" : "grid-cols-3"
              } gap-x-3 gap-y-6 w-[95.5%] py-8`}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
                  <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
                  <p className="text-gray-600 font-medium">Loading..</p>
                </div>
              ) : outlets.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
                  <p className="text-gray-600 font-medium">No outlets found</p>
                </div>
              ) : (
                outlets.map((outlet, index) => (
                  <div
                    key={outlet?.id}
                    className="bg-[#FFFFFF] rounded-lg flex items-center justify-between border-[0.5px] border-[#00000014] shadow-custom4 px-2 pl-4 py-1.5 cursor-pointer relative overflow-hidden"
                    onClick={() => handleOutletClick(outlet?.id)}
                  >
                    <div className="absolute -top-[34px] -right-[34px] w-16 h-16 bg-gradient-to-r from-[#F75E00] to-[#FF8A3D] rotate-45 shadow-lg transform group-hover:scale-110 transition-transform">
                      <div className="absolute bottom-1 left-0 right-0 text-white font-bold text-xs flex justify-center items-center rotate-[315deg]">
                        {(currentPage - 1) * 15 + index + 1}
                      </div>
                    </div>

                    <div className="flex items-center 2xl:gap-4 xl:gap-3 lg:gap-3">
                      <Avatar
                        name={outlet?.outlet_name}
                        size="52"
                        round={true}
                        maxInitials={2}
                        textSizeRatio={2.5}
                        style={{ letterSpacing: "1px" }}
                      />
                      <div className="flex flex-col 2xl:gap-1.5 xl:gap-1.5 lg:gap-1.5">
                        <p className="2xl:text-[16px] xl:text-[12px] lg:text-[11px] font-semibold tracking-wide capitalize">
                          {outlet?.outlet_name}
                        </p>
                        <p
                          className={`2xl:text-[13px] xl:text-[10px] lg:text-[9px] ${outlet?.is_active
                            ? "text-[#25AD34]"
                            : "text-[#F34B50]"
                            } font-semibold tracking-wide flex items-center gap-0.5 h-1 2xl:mb-2.5 xl:mb-1.5 lg:mb-1`}
                        >
                          <span className="2xl:pt-[3px] xl:pt-[1.5px] lg:pt-[0.5px]">
                            {outlet?.is_active ? "Active" : "Not Active"}
                          </span>
                          <span className="2xl:text-3xl xl:text-[19px] lg:text-[15px]">
                            •
                          </span>
                        </p>
                      </div>
                    </div>
                    <img
                      src="/assets/right-arrow.svg"
                      alt=""
                      className="2xl:h-[26px] xl:h-[22px] lg:h-[22px]"
                    />
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 w-[95.5%] flex justify-center">
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-4">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-[#F7631D] text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-[#F7631D] text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            {isLoading ? (
              ""
            ) : (
              <div className="w-[95.5%] flex justify-center py-10">
                <PrimaryButton
                  text={`New outlet requests (${newOutlets?.count || 0})`}
                  uppercase={false}
                  path="/manage-outlets"
                  width="2xl:w-[30%] xl:w-[35%] lg:w-[40%] whitespace-nowrap"
                />
              </div>
            )}
          </div>

          <div className="w-[48%] h-screen">
            <div className="2xl:w-[100%] xl:w-full lg:w-full md:w-full sm:w-full h-[92%] rounded-xl flex justify-center items-center overflow-hidden shadow-default">
              {isLoading ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              ) : (
                <Map
                  key={mapKey}
                  mapId={"terrain"}
                  defaultZoom={6}
                  defaultCenter={mapCenter}
                  gestureHandling={"greedy"}
                  onClick={onMapClick}
                  disableDefaultUI
                  styles={"terrain"}
                  fullscreenControl={true}
                  mapTypeId={"terrain"}
                  isFractionalZoomEnabled={true}
                  mapTypeControlOptions
                  zoomControl
                  scaleControl
                  streetViewControl
                  mapTypeControl
                  cameraControl
                  rotateControl
                >
                  <MapMarker
                    data={outlets}
                    entityType="outlets"
                    onMouseEnter={handleMarkerClick}
                  />

                  <InfoWindowPopup
                    infoWindowShown={infoWindowShown}
                    selectedMarker={selectedMarker}
                    handleCloseInfoWindow={handleCloseInfoWindow}
                  />
                </Map>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Outlets;
